<template>
	<div class="business-directory-categories-manager">
		<div class="d-flex align-items-center mb-3">
			<h6 class="mb-0">Categories</h6>
			<button v-if="!showCreateForm" class="btn btn-sm btn-primary ms-auto" @click="openCreateForm">
				<font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
				Add category
			</button>
		</div>

		<div v-if="showCreateForm" class="border rounded p-3 mb-3 bg-light">
			<div class="row g-2 align-items-end">
				<div class="col-md-6">
					<label class="form-label">Name</label>
					<input
						ref="newNameInput"
						type="text"
						class="form-control form-control-sm"
						v-model="newCategory.name"
						placeholder="e.g. Restaurants"
						@keydown.enter.prevent="createCategory"
						@keydown.esc="cancelCreate"
					/>
				</div>
				<div class="col-md-3">
					<label class="form-label">Order</label>
					<input
						type="number"
						class="form-control form-control-sm"
						v-model.number="newCategory.order"
						min="0"
					/>
				</div>
				<div class="col-md-3 text-end">
					<button class="btn btn-sm btn-link" @click="cancelCreate" :disabled="creating">Cancel</button>
					<button class="btn btn-sm btn-primary" @click="createCategory" :disabled="!canCreate || creating">
						<font-awesome-icon v-if="creating" :icon="['fas', 'spinner']" class="fa-spin me-1" />
						Save
					</button>
				</div>
			</div>
			<div v-if="errorMessage" class="alert alert-danger py-2 mt-2 mb-0 small">{{ errorMessage }}</div>
		</div>

		<div v-if="loading" class="text-center text-muted py-4">
			<font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin me-2" />
			Loading categories…
		</div>

		<div v-else-if="!categories.length" class="text-center text-muted py-4 border rounded bg-light">
			No categories yet. Click <strong>Add category</strong> to create one.
		</div>

		<div v-else class="table-responsive">
			<table class="table table-sm align-middle">
				<thead>
					<tr>
						<th>Name</th>
						<th>Slug</th>
						<th style="width: 100px">Order</th>
						<th style="width: 150px" class="text-end">Actions</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="cat in sortedCategories" :key="cat.id">
						<td>
							<input
								v-if="editingId === cat.id"
								v-model="editBuffer.name"
								type="text"
								class="form-control form-control-sm"
								@keydown.enter.prevent="saveEdit"
								@keydown.esc="cancelEdit"
							/>
							<span v-else>{{ cat.name }}</span>
						</td>
						<td>
							<code class="small text-muted">{{ cat.slug }}</code>
						</td>
						<td>
							<input
								v-if="editingId === cat.id"
								v-model.number="editBuffer.order"
								type="number"
								class="form-control form-control-sm"
								min="0"
							/>
							<span v-else>{{ cat.order }}</span>
						</td>
						<td class="text-end">
							<template v-if="editingId === cat.id">
								<button class="btn btn-sm btn-link" @click="cancelEdit" :disabled="savingId === cat.id">Cancel</button>
								<button class="btn btn-sm btn-primary" @click="saveEdit" :disabled="savingId === cat.id">
									<font-awesome-icon v-if="savingId === cat.id" :icon="['fas', 'spinner']" class="fa-spin" />
									<span v-else>Save</span>
								</button>
							</template>
							<template v-else>
								<button class="btn btn-sm btn-link text-secondary" @click="startEdit(cat)" title="Edit">
									<font-awesome-icon :icon="['fas', 'pen']" />
								</button>
								<button class="btn btn-sm btn-link text-danger" @click="deleteCategory(cat)" :disabled="deletingId === cat.id" title="Delete">
									<font-awesome-icon v-if="deletingId === cat.id" :icon="['fas', 'spinner']" class="fa-spin" />
									<font-awesome-icon v-else :icon="['fas', 'trash']" />
								</button>
							</template>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'

export default {
	name: 'BusinessDirectoryCategoriesManager',
	props: {
		clientId: {
			type: [Number, String],
			required: true,
		},
	},
	data() {
		return {
			loading: false,
			categories: [],
			showCreateForm: false,
			newCategory: { name: '', order: 0 },
			creating: false,
			editingId: null,
			editBuffer: { name: '', order: 0 },
			savingId: null,
			deletingId: null,
			errorMessage: '',
		}
	},
	computed: {
		canCreate() {
			return this.newCategory.name && this.newCategory.name.trim().length > 0
		},
		sortedCategories() {
			return [...this.categories].sort((a, b) => {
				if (a.order !== b.order) return (a.order || 0) - (b.order || 0)
				return (a.name || '').localeCompare(b.name || '')
			})
		},
	},
	mounted() {
		this.fetchCategories()
	},
	methods: {
		async fetchCategories() {
			this.loading = true
			try {
				const { data } = await juniperApi.get('/business-directory-categories', {
					params: { client_id: this.clientId },
				})
				this.categories = Array.isArray(data) ? data : []
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Failed to load categories.'
			} finally {
				this.loading = false
			}
		},
		openCreateForm() {
			this.errorMessage = ''
			this.newCategory = { name: '', order: 0 }
			this.showCreateForm = true
			this.$nextTick(() => {
				if (this.$refs.newNameInput) this.$refs.newNameInput.focus()
			})
		},
		cancelCreate() {
			this.showCreateForm = false
			this.errorMessage = ''
		},
		async createCategory() {
			if (!this.canCreate) return
			this.creating = true
			this.errorMessage = ''
			try {
				const { data } = await juniperApi.post('/business-directory-categories', {
					client_id: this.clientId,
					name: this.newCategory.name.trim(),
					order: this.newCategory.order || 0,
				})
				this.categories.push(data)
				this.showCreateForm = false
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Failed to create category.'
			} finally {
				this.creating = false
			}
		},
		startEdit(cat) {
			this.editingId = cat.id
			this.editBuffer = { name: cat.name, order: cat.order || 0 }
		},
		cancelEdit() {
			this.editingId = null
		},
		async saveEdit() {
			const id = this.editingId
			if (!id) return
			this.savingId = id
			try {
				const { data } = await juniperApi.put(`/business-directory-categories/${id}`, {
					client_id: this.clientId,
					name: this.editBuffer.name,
					order: this.editBuffer.order || 0,
				})
				const idx = this.categories.findIndex(c => c.id === id)
				if (idx !== -1) this.$set(this.categories, idx, data)
				this.editingId = null
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Failed to save category.'
			} finally {
				this.savingId = null
			}
		},
		async deleteCategory(cat) {
			if (!window.confirm(`Delete category "${cat.name}"? Business directories will keep their data but lose this category.`)) {
				return
			}
			this.deletingId = cat.id
			try {
				await juniperApi.delete(`/business-directory-categories/${cat.id}`, {
					params: { client_id: this.clientId },
				})
				this.categories = this.categories.filter(c => c.id !== cat.id)
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Failed to delete category.'
			} finally {
				this.deletingId = null
			}
		},
	},
}
</script>
